import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { orderService } from '../services/api';
import { FaArrowLeft, FaTruck, FaMapMarkerAlt, FaFileInvoice } from 'react-icons/fa';
import { Button, Modal } from 'react-bootstrap';
import Invoice from '../components/Invoice';

const OrderDetailPage = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const response = await orderService.getOrderById(id);
        setOrder(response.data);
      } catch (error) {
        console.error('Error fetching order:', error);
        setError('Failed to load order details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id]);

  // Get status badge class based on order status
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'processing':
        return 'bg-primary-color';
      case 'shipped':
        return 'bg-accent-color';
      case 'delivered':
        return 'bg-success-color';
      case 'cancelled':
        return 'bg-danger-color';
      default:
        return 'bg-secondary-color';
    }
  };
  
  // Get status icon based on order status
  const getStatusIcon = (status) => {
    switch (status) {
      case 'processing':
        return '🔧';
      case 'shipped':
        return '📦';
      case 'delivered':
        return '✅';
      case 'cancelled':
        return '❌';
      default:
        return '🕒';
    }
  };

  if (loading) {
    return (
      <div className="container py-4">
        <div className="text-center">
          <p>Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="container py-4">
        <div className="alert alert-error">
          <p>{error || 'Order not found'}</p>
          <Link to="/orders" className="btn btn-primary mt-4">
            Return to Orders
          </Link>
        </div>
      </div>
    );
  }

  const { order: orderData, items } = order;

  return (
    <div className="container py-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Order #{orderData.id}</h1>
        <Link to="/orders" className="flex items-center text-primary-color">
          <FaArrowLeft className="mr-2" /> Back to Orders
        </Link>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Order info */}
        <div className="md:col-span-2">
          <div className="card mb-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-bold">Order Details</h2>
                <p className="text-sm">
                  Placed on {new Date(orderData.created_at).toLocaleDateString()}
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                <span className={`${getStatusBadgeClass(orderData.status)} text-white py-1 px-3 rounded-full flex items-center gap-2`}>
                  {getStatusIcon(orderData.status)} {orderData.status.charAt(0).toUpperCase() + orderData.status.slice(1)}
                </span>
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  onClick={() => setShowInvoiceModal(true)}
                  className="d-flex align-items-center gap-2"
                >
                  <FaFileInvoice /> Invoice
                </Button>
              </div>
            </div>
            
            <div className="border-b border-light-gray mb-4"></div>
            
            <div className="flex items-start gap-4 mb-4">
              <div className="text-primary-color">
                <FaTruck size={24} />
              </div>
              <div>
                <h3 className="font-bold">Shipping Information</h3>
                <p className="text-sm">{orderData.shipping_address}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="text-primary-color">
                <FaMapMarkerAlt size={24} />
              </div>
              <div>
                <h3 className="font-bold">Delivery Status</h3>
                <p className="text-sm">
                  {orderData.status === 'delivered' 
                    ? 'Your order has been delivered' 
                    : orderData.status === 'shipped'
                    ? 'Your order is on the way'
                    : orderData.status === 'processing'
                    ? 'Your order is being processed'
                    : orderData.status === 'cancelled'
                    ? 'Your order has been cancelled'
                    : 'Your order is pending'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <h2 className="text-xl font-bold mb-4">Order Items</h2>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-light-gray">
                    <th className="text-left py-2">Product</th>
                    <th className="text-center py-2">Quantity</th>
                    <th className="text-right py-2">Price</th>
                    <th className="text-right py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className="border-b border-light-gray">
                      <td className="py-4">
                        <div className="flex items-center">
                          {item.image_url && (
                            <img 
                              src={item.image_url} 
                              alt={item.name}
                              className="w-12 h-12 object-cover rounded mr-3"
                            />
                          )}
                          <div>
                            <p className="font-medium">{item.name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 text-center">{item.quantity}</td>
                      <td className="py-4 text-right">${parseFloat(item.price).toFixed(2)}</td>
                      <td className="py-4 text-right">${parseFloat(item.price * item.quantity).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        
        {/* Order summary */}
        <div className="md:col-span-1">
          <div className="card">
            <h2 className="text-xl font-bold mb-4">Order Summary</h2>
            
            <div className="flex justify-between mb-2">
              <span>Subtotal</span>
              <span>${parseFloat(orderData.total_amount).toFixed(2)}</span>
            </div>
            
            <div className="flex justify-between mb-2">
              <span>Shipping</span>
              <span>Free</span>
            </div>
            
            <div className="flex justify-between mb-2">
              <span>Tax</span>
              <span>$0.00</span>
            </div>
            
            <div className="border-t border-light-gray my-4"></div>
            
            <div className="flex justify-between font-bold text-lg">
              <span>Total</span>
              <span>${parseFloat(orderData.total_amount).toFixed(2)}</span>
            </div>
            
            <div className="mt-6">
              <Link to="/products" className="btn btn-primary w-full">
                Buy Again
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Modal */}
      <Modal 
        show={showInvoiceModal} 
        onHide={() => setShowInvoiceModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Invoice #{orderData?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Invoice 
            order={{
              id: orderData.id,
              date: new Date(orderData.created_at).toLocaleDateString(),
              status: orderData.status,
              total: parseFloat(orderData.total_amount)
            }}
            items={items.map(item => ({
              id: item.id,
              name: item.name,
              price: parseFloat(item.price),
              quantity: item.quantity
            }))}
            customerDetails={{
              name: orderData.name,
              address: orderData.shipping_address,
              email: orderData.email || 'customer@example.com',
              phone: orderData.phone || 'N/A'
            }}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default OrderDetailPage; 